import * as React from 'react';
import { useMemo } from 'react';
import { styled, alpha, SxProps, Theme, createTheme, ThemeProvider } from '@mui/material/styles';
import Button from '@mui/material/Button';
import Menu, { MenuProps } from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { SideSheetGroupedSelectOptionGroup } from '../side-sheet/side-sheet-grouped-select/SideSheetGroupedSelect';
import { useTranslation } from 'react-i18next';
import { Box, ListSubheader, Stack, Tooltip, Typography } from '@mui/material';
import { publish } from "pubsub-js";
import { CONFIGURATION_CHANGED_TOPIC, ConfigurationChangedMsg } from '../../common/models/pub-sub/PubSubTopics';
import { isMobile, isTablet } from 'react-device-detect';
import { useAppSettings } from '../../contexts/app-context/AppContext';
import NavigationFilterInput from '../navigation-filter/NavigationFilterInput';

const StyledMenu = styled((props: MenuProps) => (
    <Menu
        elevation={0}
        anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
        }}
        transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
        }}
        {...props}
    />
))(({ theme }) => ({
        '& .MuiPaper-root': {
        borderRadius: 6,
        //marginTop: theme.spacing(1),
        marginTop: 0,
        minWidth: 200,
        color: 'rgb(55, 65, 81)',
        boxShadow:
            'rgb(255, 255, 255) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 0px 0px 1px, rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px',
        '& .MuiMenu-list': {
            padding: '4px 0',
        },
        '& .MuiMenuItem-root': {
            '& .MuiSvgIcon-root': {
            fontSize: 18,
            color: theme.palette.text.secondary,
            marginRight: theme.spacing(1.5),
            ...theme.applyStyles('dark', {
                color: 'inherit',
            }),
            },
            '&:active': {
            backgroundColor: alpha(
                theme.palette.primary.main,
                theme.palette.action.selectedOpacity,
            ),
            },
        },
        ...theme.applyStyles('dark', {
            color: theme.palette.grey[300],
        }),
        },
}));


interface OptionGroupHeadingProps {
    name?: string;
}

function OptionGroupHeading({ name }: OptionGroupHeadingProps) {
    return (
        <Box component="span" sx={{
            fontSize: "1rem",
            textTransform: "uppercase",
            display: "block",
        }}>
            {name}
        </Box>
    );
}

interface OptionNameProps {
    name?: string;
    isDefault?: boolean;
    startAdornment?: React.ReactNode;
    endAdornment?: React.ReactNode;
    sx?: SxProps<Theme>;
    createdBy?: string;
}

function OptionName({
    name,
    isDefault,
    startAdornment,
    endAdornment,
    sx,
    createdBy

}: OptionNameProps) {
    function renderSegment() {
        return (
            <Stack direction="row" sx={{ flex: "1 1 auto", alignItems: "center" }} spacing={1}>
                {startAdornment && <Box sx={{ flex: "0 0 auto", display: "flex", alignItems: "center" }}>{startAdornment}</Box>}
                <Box sx={{ flex: "1 1 auto", display: "flex", alignItems: "center" }}>{name}{isDefault && <Default />}</Box>
                {endAdornment && <Box sx={{ flex: "0 0 auto", display: "flex", alignItems: "center" }}>{endAdornment}</Box>}
            </Stack>
        );
    }

    return (
        <Box
            sx={{
                paddingLeft: 2,
                position: "relative",
                flex: "1 1 auto",
                display: "flex",
                alignItems: "center",
                ...sx,
            }}
        >
            {createdBy 
                ? <Tooltip title={`Owner - ${createdBy}`} placement='right' arrow={true}>
                    {renderSegment()}
                  </Tooltip>
                : renderSegment()
            }
        </Box>
    );
}

function Default() {

    const { t } = useTranslation();
    return (
        <Typography variant="overline" sx={{
            marginLeft: 2,
            opacity: "0.7",
            textTransform: "uppercase",
            marginRight: 'auto'
        }}>
            {t("configurationList.default")}
        </Typography>
    );
}



export type ContextConfigurationSelectProps = {
    optionGroups: SideSheetGroupedSelectOptionGroup<number>[];
    configurationID: number;
    filterText: string;
    setFilterText: (value: string) => void;
    hideFilter: boolean;
};

export default function ContextConfigurationSelect({
    optionGroups,
    configurationID,
    filterText,
    setFilterText,
    hideFilter
}: ContextConfigurationSelectProps) {

    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const isPhone = isMobile && !isTablet;
     const [appSettings] = useAppSettings();

    const open = Boolean(anchorEl);

    // const theme = React.useMemo(
    //     () => {
    //         return isPhone ? createTheme(appSettings.themeSet[appSettings.themeSet.mode].appHeaderV1) :
    //             createTheme(appSettings.themeSet[appSettings.themeSet.mode].appHeaderV2);
    //     },
    //     [appSettings.themeSet,
    //         isPhone]
    // );

    const mainTheme = useMemo(() => {
        return createTheme(appSettings.themeSet[appSettings.themeSet.mode].main);
    }, [appSettings.themeSet]);

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    function onClose() {

        setAnchorEl(null);
    };

    function onSelected(newValue: number) {

        if (newValue !== configurationID) {

            publish(CONFIGURATION_CHANGED_TOPIC, {
                configurationID: newValue
            } as ConfigurationChangedMsg);

        }

        setAnchorEl(null);
    }

    const selectedItem = optionGroups.flatMap(o => o.options).find(o => o.value === configurationID);

    return (
        <Box
            sx={{
                marginRight: 1,
                visibility: optionGroups.length === 0 ? 'hidden' : undefined
            }}>
            <Button
                id="configuration-menu-button"
                aria-controls={open ? 'configurations-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={open ? 'true' : undefined}
                variant="outlined"
                disableElevation
                onClick={handleClick}
                endIcon={<KeyboardArrowDownIcon sx={{ marginLeft: 'auto' }} />}
                sx={{
                    height: '28px', // have to force the height to squish it down for the stupid appbar area
                    //maxWidth: '250px',
                    //minWidth: '200px',
                    width: optionGroups.length > 0 ? '250px' : '100px',
                    justifyContent: 'flex-start',
                    textTransform: "none",
                    // borderColor: isPhone ? theme => theme.palette.text.primary : undefined,
                    // color:  isPhone ? theme => theme.palette.text.primary : undefined
                    borderColor: 'inherit',
                    color: 'inherit',
                }}
            >
                {
                    optionGroups.length > 0 &&
                    <Typography
                        variant='body2'
                        component='div'
                        sx={{
                            marginTop: '4px',
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            marginRight: 'auto'
                        }}
                    >
                        {selectedItem?.label}{selectedItem?.isDefault && <Default />}
                    </Typography>
                }
            </Button>
            <ThemeProvider theme={mainTheme}>
                <StyledMenu
                    id="configurations-menu"
                    autoFocus={false}
                    slotProps={{
                        list: {
                            'aria-labelledby': 'configurations-menu-button',
                        },
                    }}
                    anchorEl={anchorEl}
                    open={open}
                    onClose={onClose}
                >
                    {!hideFilter &&
                        <NavigationFilterInput
                            value={filterText}
                            onChange={setFilterText}
                            stopPropagation={true}
                            sx={{
                                width: '95%',
                                minWidth: '340px',
                                marginLeft: 1,
                                marginRight: 1
                            }}
                            autoFocus={true}
                        />
                    }

                    {
                        optionGroups?.map((group, groupIndex) => [
                            <ListSubheader key={`g${groupIndex}`}>
                                <OptionGroupHeading name={group.heading} />
                            </ListSubheader>,

                            group.options?.map((configuration, configurationIndex) =>
                                <MenuItem
                                    key={`g${groupIndex}c${configurationIndex}`}
                                    value={configuration.value}
                                    onClick={() => onSelected(configuration.value)}
                                    selected={configuration.value === configurationID}
                                >

                                    <OptionName
                                        name={configuration.label}
                                        isDefault={configuration.isDefault}
                                        startAdornment={configuration.startAdornment}
                                        endAdornment={configuration.endAdornment}
                                        createdBy={configuration.createdUserName}
                                        sx={group.heading.length === 0 ? {
                                            paddingLeft: 0
                                        } : undefined}
                                    />

                                </MenuItem>
                            )
                        ])}

                </StyledMenu>
            </ThemeProvider>
        </Box>
    );
}
